import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useMediaQuery } from '@chakra-ui/react'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useGetHeaderInfoQuery } from '@core/app/api/profileApi'
import { Localization } from '@core/app/types/globalApiType'
import type { ApplyJobSettingsV2 } from '@core/app/types/postsApiType'
import ApplyV2Picture from '@core/components/ApplyDialogV2/ApplyV2Picture'
import Button from '@core/components/ApplyDialogV2/v2/Button'
import FeedbackTop from '@core/components/Header/FeedbackTop'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import useLocalStorageStore from '@core/utils/hooks/useLocalStorageStore'

import PostPageHeader2ApplyStatus from '@page/post-v2/components/PostPageV2Header2/PostPageHeader2ApplyStatus'
import type { ApplicationDetailsV2, PostInfoV2 } from '@page/post-v2/types'

import './PostPageV2Header2.scss'

const baseClass = 'PostPageV2Header2'

const mobileExpandedHeaderMinHeightPx = 200
const tabletExpandedHeaderMinHeightPx = 340
const desktopExpandedHeaderMaxViewportRatio = 0.35
const collapsedHeaderMinHeightPx = 76
const headerIntroExtraHeightPx = 50

interface PostPageV2HeaderProps {
    postInfo?: PostInfoV2 | undefined | null
    isLoading?: boolean
    isApplicationDetailsLoading?: boolean
    postApplySettingsData?: ApplyJobSettingsV2 | undefined | null
    applicationDetailsData?: ApplicationDetailsV2 | undefined | null
    statusNotice?: React.ReactNode
    isPreview: boolean
}

function PostPageV2Header2({
    postInfo,
    isLoading,
    postApplySettingsData,
    applicationDetailsData,
    isApplicationDetailsLoading,
    isPreview,
    statusNotice,
}: PostPageV2HeaderProps) {
    const [hasInlineStatusLayout, isDesktopLayout] = useMediaQuery(['(min-width: 601px)', '(min-width: 1025px)'])
    const { id } = useParams()
    const { onOpenApply, setPostInfoBackgroundColor } = useApplyV2Context()
    const { t } = useTranslation()
    const numberId = useMemo(() => {
        const n = Number(id)
        return isNaN(n) ? 0 : n
    }, [id])
    const { data: headerData } = useGetHeaderInfoQuery(undefined, { skip: !numberId })
    const candidateId = headerData?.data?.CandidateID ?? 0
    const applyV2Key = useMemo(() => `apply_dialog_v2-${numberId}-${candidateId}` as const, [numberId, candidateId])
    const [storageData] = useLocalStorageStore(applyV2Key)
    const [localization] = useState<Localization>({})
    const [isFeedbackMenu, setIsFeedbackMenu] = useState(false)
    const [selectedFeedback, setSelectedFeedback] = useState<number | null>(null)
    const backgroundImage = postInfo?.data?.cover?.backgroundImage || postInfo?.data?.logo
    const backgroundColor = postInfo?.data?.cover?.color ?? '#FFF'
    const hasHeaderBackground = Boolean(backgroundImage || postInfo?.data?.cover?.color)
    const [isCollapsed, setIsCollapsed] = useState(false)
    const getExpandedHeaderMinHeightPx = () => {
        if (typeof window === 'undefined') {
            return tabletExpandedHeaderMinHeightPx
        }

        if (window.innerWidth <= 600) {
            return mobileExpandedHeaderMinHeightPx
        }

        if (window.innerWidth <= 1024) {
            return tabletExpandedHeaderMinHeightPx
        }

        return Math.max(
            collapsedHeaderMinHeightPx,
            Math.floor(window.innerHeight * desktopExpandedHeaderMaxViewportRatio)
        )
    }
    const [introHeight, setIntroHeight] = useState<number>(
        () => getExpandedHeaderMinHeightPx() + headerIntroExtraHeightPx
    )
    const [scrollY, setScrollY] = useState(0)
    const feedbackRef = useRef<HTMLDivElement>(null)
    const noticeRef = useRef<HTMLDivElement>(null)
    const toolbarRef = useRef<HTMLDivElement>(null)
    const [feedbackHeight, setFeedbackHeight] = useState(0)
    const [noticeHeight, setNoticeHeight] = useState(0)
    const [viewportSize, setViewportSize] = useState(0)

    useLayoutEffect(() => {
        const measure = () => {
            setFeedbackHeight(feedbackRef.current?.getBoundingClientRect().height ?? 0)
            setNoticeHeight(noticeRef.current?.getBoundingClientRect().height ?? 0)
        }
        const observer = new ResizeObserver(measure)
        if (feedbackRef.current) observer.observe(feedbackRef.current)
        if (noticeRef.current) observer.observe(noticeRef.current)
        measure()
        return () => observer.disconnect()
    }, [isLoading, statusNotice, hasInlineStatusLayout])

    useEffect(() => {
        const resize = () => setViewportSize((version) => version + 1)
        window.addEventListener('resize', resize)
        return () => window.removeEventListener('resize', resize)
    }, [])
    const hasAnimatedOnLoad = useRef(false)
    const applyStatusBoundaryRef = useRef<HTMLDivElement | null>(null)
    const applicationStatusId = applicationDetailsData?.data?.application?.statusId
    const isLogoExist = useMemo(() => !!postInfo?.data?.cover?.logo, [postInfo?.data?.cover?.logo])
    const expandedHeaderMinHeightPx = getExpandedHeaderMinHeightPx()
    const fallbackCollapseThresholdPx = expandedHeaderMinHeightPx - collapsedHeaderMinHeightPx + 10

    const { isArchived, isEmployeesOnly, isExpired, isExternal } = postInfo?.data || {}

    const isUnavailable = isArchived || isEmployeesOnly || isExpired

    const getCollapseThreshold = () => {
        if (!applyStatusBoundaryRef.current) {
            return fallbackCollapseThresholdPx
        }

        const boundaryRect = applyStatusBoundaryRef.current.getBoundingClientRect()

        return Math.max(
            0,
            boundaryRect.bottom +
                window.scrollY -
                (toolbarRef.current?.getBoundingClientRect().height ?? collapsedHeaderMinHeightPx)
        )
    }

    const applyButtonTitle = useMemo(() => {
        if (storageData) return t('11868', 'Continue application')
        return localization[30]
            ? localization[30]
            : t('6634', 'Apply now')
                  .toLowerCase()
                  .replace(/^\w/, (c) => c.toUpperCase())
    }, [localization, storageData, t])

    useEffect(() => {
        setPostInfoBackgroundColor(postInfo?.data?.cover?.color)
    }, [postInfo?.data?.cover?.color])

    useEffect(() => {
        let frameId = 0

        const onScroll = () => {
            if (frameId) return
            frameId = window.requestAnimationFrame(() => {
                const nextScrollY = window.scrollY
                const collapseThresholdPx = getCollapseThreshold()
                setScrollY(nextScrollY)
                setIsCollapsed(nextScrollY >= collapseThresholdPx)
                frameId = 0
            })
        }

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
            if (frameId) window.cancelAnimationFrame(frameId)
        }
    }, [feedbackHeight, noticeHeight, viewportSize, isLoading])

    useLayoutEffect(() => {
        if (isLoading || hasAnimatedOnLoad.current) return

        hasAnimatedOnLoad.current = true
        setIntroHeight(expandedHeaderMinHeightPx + headerIntroExtraHeightPx)

        const shrinkTimer = window.setTimeout(() => {
            setIntroHeight(expandedHeaderMinHeightPx)
        }, 300)

        return () => {
            window.clearTimeout(shrinkTimer)
        }
    }, [expandedHeaderMinHeightPx, isLoading])

    useEffect(() => {
        if (!hasAnimatedOnLoad.current) return
        setIntroHeight(expandedHeaderMinHeightPx)
    }, [expandedHeaderMinHeightPx])

    const onApply = () => {
        if (postInfo?.data?.isExternal && postInfo?.data?.externalLink && isExternal) {
            window.open(postInfo.data.externalLink)
        } else {
            onOpenApply()
        }
    }

    const headerStyle = useMemo(
        () =>
            ({
                backgroundColor,
                paddingTop: feedbackHeight + (hasInlineStatusLayout ? collapsedHeaderMinHeightPx : 0),
                minHeight: hasHeaderBackground
                    ? `${introHeight + feedbackHeight + (isDesktopLayout ? noticeHeight : 0)}px`
                    : undefined,
                borderBottom: isCollapsed ? '1px solid #EAEAEB' : undefined,
                '--header-collapse-progress': isCollapsed ? '1' : '0',
                '--header-white-overlay-opacity': isCollapsed ? '1' : '0',
                '--header-parallax-offset': `${Math.min(scrollY * 0.32, 120)}px`,
                '--header-bg-image': backgroundImage ? `url(${backgroundImage})` : 'none',
                transition: 'min-height 700ms ease',
            }) as React.CSSProperties,
        [
            hasInlineStatusLayout,
            isDesktopLayout,
            backgroundColor,
            backgroundImage,
            hasHeaderBackground,
            introHeight,
            feedbackHeight,
            noticeHeight,
            isCollapsed,
            scrollY,
        ]
    )

    const isProductionCondition =
        !isUnavailable &&
        !isApplicationDetailsLoading &&
        applicationStatusId !== 0 &&
        applicationStatusId !== 1 &&
        applicationStatusId !== 2 &&
        applicationStatusId !== 3 &&
        applicationStatusId !== 4

    const isLocalCondition =
        process.env.ENV === 'local' &&
        !isUnavailable &&
        applicationStatusId !== undefined &&
        [0, 1, 2, 3, 4].includes(applicationStatusId)

    const showButton = isProductionCondition || isLocalCondition

    if (isLoading) return <header className={baseClass}></header>

    return (
        <>
            {!hasInlineStatusLayout && statusNotice}
            <header className={baseClass} data-bg={hasHeaderBackground} style={headerStyle}>
                {hasInlineStatusLayout && (
                    <div ref={noticeRef} className={`${baseClass}__notice`}>
                        {statusNotice}
                    </div>
                )}
                {isLogoExist && (
                    <div
                        className={clsx(`${baseClass}__info`, {
                            [`${baseClass}__info__collapsed`]: isCollapsed,
                        })}
                    >
                        <div
                            className={clsx(`${baseClass}__info__logo`, {
                                [`${baseClass}__info__logo--hidden`]: !isLogoExist,
                                [`${baseClass}__info__logo--collapsed`]: isCollapsed,
                                [`${baseClass}__info__logo--noBackground`]: !hasHeaderBackground,
                            })}
                        >
                            <ApplyV2Picture
                                fallbackSrc={postInfo?.data?.cover?.logo ?? ''}
                                alt={postInfo?.data?.jobPostingName ?? ''}
                                loading={'lazy'}
                            />
                        </div>
                    </div>
                )}
                <div
                    ref={toolbarRef}
                    className={clsx(`${baseClass}__top`, {
                        [`${baseClass}__top--collapsed`]: isCollapsed,
                        [`${baseClass}__top--scrolled`]: isCollapsed,
                    })}
                >
                    <div ref={feedbackRef}>
                        <FeedbackTop
                            isProfilePage={false}
                            isMenuFeedback={isFeedbackMenu && !isPreview}
                            onClose={() => {
                                setIsFeedbackMenu(false)
                                setSelectedFeedback(null)
                            }}
                            setSelectedFeedback={setSelectedFeedback}
                            selectedFeedback={selectedFeedback}
                        />
                    </div>
                    <div
                        className={clsx(`${baseClass}__top__wrap`, {
                            [`${baseClass}__top__wrap--collapsed`]: isCollapsed,
                            [`${baseClass}__top__wrap--scrolled`]: isCollapsed,
                        })}
                    >
                        <div className={`${baseClass}__action`}>
                            <Button
                                className={`${baseClass}__action--allJobs`}
                                variant="secondary"
                                type="button"
                                onClick={() => {
                                    window.location.href = '/jobs'
                                }}
                                leftIcon={
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M7.09458 11.5742C7.32889 11.8085 7.32889 12.1884 7.09458 12.4227C6.86026 12.657 6.48036 12.657 6.24605 12.4227L2.24605 8.4227C2.13353 8.31018 2.07031 8.15757 2.07031 7.99844C2.07031 7.83931 2.13353 7.6867 2.24605 7.57417L6.24605 3.57417C6.48036 3.33986 6.86026 3.33986 7.09458 3.57417C7.32889 3.80849 7.32889 4.18839 7.09458 4.4227L4.11884 7.39844H13.337C13.6683 7.39844 13.937 7.66707 13.937 7.99844C13.937 8.32981 13.6683 8.59844 13.337 8.59844H4.11884L7.09458 11.5742Z"
                                            fill="#19191A"
                                        />
                                    </svg>
                                }
                            >
                                {t('6926', 'All jobs')}
                            </Button>
                        </div>
                        <div className={`${baseClass}__jobPostingName--title--fixed`}>
                            {decodeHtmlEntitiesRecursive(postInfo?.data?.jobPostingName)}
                        </div>
                        {showButton ? (
                            <div className={`${baseClass}__action ${baseClass}__action--apply`}>
                                <Button
                                    variant="primary"
                                    id={'ra-button-job-post-action-apply-now'}
                                    ra-data-job-post-id={id}
                                    onClick={() => onApply()}
                                    disabled={isPreview}
                                >
                                    {applyButtonTitle}
                                </Button>
                            </div>
                        ) : (
                            <div className={`${baseClass}__empty`}></div>
                        )}
                    </div>
                </div>
            </header>
            <div
                className={clsx(`${baseClass}__bottom`, {
                    [`${baseClass}__bottom--collapsed`]: isCollapsed,
                    [`${baseClass}__bottom--noBackground`]: !hasHeaderBackground && !postInfo?.data?.cover?.logo,
                })}
            >
                <div ref={applyStatusBoundaryRef}>
                    <PostPageHeader2ApplyStatus
                        postApplySettingsData={postApplySettingsData}
                        applicationDetailsData={applicationDetailsData}
                        isLoading={isLoading}
                        postInfo={postInfo}
                    />
                </div>
                {/*<div className={`${baseClass}__jobPostingName`}>*/}
                {/*    <div className={`${baseClass}__jobPostingName--title`}>*/}
                {/*        {decodeHtmlEntitiesRecursive(postInfo?.data?.jobPostingName)}*/}
                {/*    </div>*/}
                {/*</div>*/}
            </div>
        </>
    )
}

export default PostPageV2Header2
